import { useState } from 'react';
import { type Control, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleButtons } from '@/components/ui/toggle-buttons';
import type { ConditionOperator, FieldType, ScreenLayout } from '@/db/schema';

const FIELD_TYPES: FieldType[] = [
  'string',
  'password',
  'number',
  'currency',
  'boolean',
  'date',
  'datetime',
  'enum',
  'relation',
  'multi-relation',
  'component',
  'file',
  'avatar',
  'email',
  'phone',
];

const FILTER_TYPES = ['text', 'number', 'select', 'date', 'period'] as const;

/** Tipos pra quem máscara (ver src/lib/mask.ts) faz sentido — o resto (avatar, boolean, relação...) não mostra a opção. */
const MASKABLE_TYPES: FieldType[] = ['string', 'number', 'currency', 'phone'];

const WIDTH_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1));

const EDIT_BEHAVIOR_OPTIONS = [
  { value: 'editable', label: 'Editável' },
  { value: 'disabled', label: 'Desabilitar' },
  { value: 'hidden', label: 'Esconder' },
] as const;
type EditBehavior = (typeof EDIT_BEHAVIOR_OPTIONS)[number]['value'];

function TypeSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

/** Editor da lista de colunas (grid de listagem). Sem drag-and-drop — quem reordena usa o campo "Ordem". */
export function ColumnsEditor({ control }: { control: Control<any> }) {
  const columns = useFieldArray({ control, name: 'layout.columns' });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Colunas exibidas na tabela de listagem.
        </p>
        <Field className="ml-auto mr-2 w-44">
          <FieldLabel>Registros por página</FieldLabel>
          <Input
            type="number"
            {...control.register('layout.perPage', {
              setValueAs: (v) => (v === '' || v == null ? undefined : Number(v)),
            })}
            placeholder="10"
          />
        </Field>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            columns.append({
              name: '',
              label: '',
              type: 'string',
              sortable: true,
              order: columns.fields.length,
            })
          }
        >
          Adicionar coluna
        </Button>
      </div>

      {columns.fields.map((row, index) => (
        <div
          key={row.id}
          className="rounded-[14px] border border-border bg-field/40 p-3 space-y-3"
        >
          <div className="grid grid-cols-12 gap-2 items-end">
            <Field className="col-span-3">
              <FieldLabel>Campo</FieldLabel>
              <Input
                {...control.register(`layout.columns.${index}.name`)}
                placeholder="nome"
              />
            </Field>
            <Field className="col-span-3">
              <FieldLabel>Label</FieldLabel>
              <Input
                {...control.register(`layout.columns.${index}.label`)}
                placeholder="Nome"
              />
            </Field>
            <Field className="col-span-3">
              <FieldLabel>Tipo</FieldLabel>
              <ColumnTypeControl control={control} index={index} />
            </Field>
            <Field className="col-span-2">
              <FieldLabel>Ordem</FieldLabel>
              <Input
                type="number"
                {...control.register(`layout.columns.${index}.order`, {
                  valueAsNumber: true,
                })}
                placeholder="0"
              />
            </Field>
            <Button
              type="button"
              size="icon"
              variant="destructive"
              icon="trash-2"
              className="col-span-1"
              onClick={() => columns.remove(index)}
            />
          </div>

          <Field className="max-w-56">
            <FieldLabel>Ordenação (clique no cabeçalho)</FieldLabel>
            <SortableControl control={control} index={index} />
          </Field>

          <ColumnExtras control={control} index={index} />
        </div>
      ))}
    </div>
  );
}

function ColumnExtras({ control, index }: { control: Control<any>; index: number }) {
  const type: FieldType = useWatch({ control, name: `layout.columns.${index}.type` });

  return (
    <>
      {(type === 'relation' || type === 'multi-relation') && (
        <RelationInputs control={control} listName="columns" index={index} />
      )}
      {MASKABLE_TYPES.includes(type) && (
        <MaskListInput control={control} name={`layout.columns.${index}.mask`} />
      )}
      {/* boolean: opções com value 'true'/'false' trocam o Sim/Não do badge (ex: Ativo/Inativo). */}
      {(type === 'enum' || type === 'boolean') && (
        <OptionsListInput control={control} name={`layout.columns.${index}.options`} />
      )}
      {type === 'component' && (
        <Field>
          <FieldLabel>Componente (chave no registry)</FieldLabel>
          <Input
            {...control.register(`layout.columns.${index}.component`)}
            placeholder="ex: address-cep"
          />
        </Field>
      )}
    </>
  );
}

/** Editor da lista de filtros da listagem. */
export function FiltersEditor({ control }: { control: Control<any> }) {
  const filters = useFieldArray({ control, name: 'layout.filters' });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Filtros disponíveis na gaveta de busca da listagem.
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            filters.append({
              name: '',
              label: '',
              type: 'text',
              order: filters.fields.length,
            })
          }
        >
          Adicionar filtro
        </Button>
      </div>

      {filters.fields.map((row, index) => (
        <div
          key={row.id}
          className="rounded-[14px] border border-border bg-field/40 p-3 space-y-3"
        >
          <div className="grid grid-cols-12 gap-2 items-end">
            <Field className="col-span-3">
              <FieldLabel>Campo</FieldLabel>
              <Input
                {...control.register(`layout.filters.${index}.name`)}
                placeholder="nome"
              />
            </Field>
            <Field className="col-span-3">
              <FieldLabel>Label</FieldLabel>
              <Input
                {...control.register(`layout.filters.${index}.label`)}
                placeholder="Nome"
              />
            </Field>
            <Field className="col-span-3">
              <FieldLabel>Tipo</FieldLabel>
              <FilterTypeControl control={control} index={index} />
            </Field>
            <Field className="col-span-2">
              <FieldLabel>Ordem</FieldLabel>
              <Input
                type="number"
                {...control.register(`layout.filters.${index}.order`, {
                  valueAsNumber: true,
                })}
                placeholder="0"
              />
            </Field>
            <Button
              type="button"
              size="icon"
              variant="destructive"
              icon="trash-2"
              className="col-span-1"
              onClick={() => filters.remove(index)}
            />
          </div>

          <FilterExtras control={control} index={index} />
        </div>
      ))}
    </div>
  );
}

function FilterExtras({ control, index }: { control: Control<any>; index: number }) {
  const type = useWatch({ control, name: `layout.filters.${index}.type` });

  if (type !== 'select') return null;

  return (
    <>
      <RelationInputs control={control} listName="filters" index={index} />
      <OptionsListInput control={control} name={`layout.filters.${index}.options`} />
    </>
  );
}

/** Editor da lista de campos do formulário — o mais completo dos 3. */
export function FieldsEditor({ control }: { control: Control<any> }) {
  const fields = useFieldArray({ control, name: 'layout.fields' });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Campos do formulário de criação/edição.
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            fields.append({
              name: '',
              label: '',
              type: 'string',
              required: false,
              width: 4,
              order: fields.fields.length,
              showOnCreate: true,
              showOnEdit: true,
              showOnView: true,
              disabledOnEdit: false,
            })
          }
        >
          Adicionar campo
        </Button>
      </div>

      {fields.fields.map((row, index) => (
        <div
          key={row.id}
          className="rounded-[14px] border border-border bg-field/40 p-4 space-y-3"
        >
          <div className="grid grid-cols-12 gap-2 items-end">
            <Field className="col-span-4">
              <FieldLabel>Campo</FieldLabel>
              <Input
                {...control.register(`layout.fields.${index}.name`)}
                placeholder="nome"
              />
            </Field>
            <Field className="col-span-4">
              <FieldLabel>Label</FieldLabel>
              <Input
                {...control.register(`layout.fields.${index}.label`)}
                placeholder="Nome"
              />
            </Field>
            <Field className="col-span-3">
              <FieldLabel>Tipo</FieldLabel>
              <FieldTypeControl control={control} index={index} />
            </Field>
            <Button
              type="button"
              size="icon"
              variant="destructive"
              icon="trash-2"
              className="col-span-1"
              onClick={() => fields.remove(index)}
            />
          </div>

          <div className="grid grid-cols-12 gap-3">
            <Field className="col-span-1">
              <FieldLabel>Ordem</FieldLabel>
              <Input
                type="number"
                {...control.register(`layout.fields.${index}.order`, {
                  valueAsNumber: true,
                })}
                placeholder="0"
              />
            </Field>
            <Field className="col-span-2">
              <FieldLabel>Largura (1-12)</FieldLabel>
              <WidthControl control={control} index={index} />
            </Field>
            <Field className="col-span-3">
              <FieldLabel>Obrigatório</FieldLabel>
              <RequiredControl control={control} index={index} />
            </Field>
            <Field className="col-span-3">
              <FieldLabel>Criação</FieldLabel>
              <CreateBehaviorControl control={control} index={index} />
            </Field>
            <Field className="col-span-3">
              <FieldLabel>Edição</FieldLabel>
              <EditBehaviorControl control={control} index={index} />
            </Field>
          </div>

          <FieldExtras control={control} index={index} />

          <div className="grid grid-cols-2 gap-3">
            <ConditionsInput
              control={control}
              name={`layout.fields.${index}.visibleIf`}
              label="Exibir somente se"
            />
            <ConditionsInput
              control={control}
              name={`layout.fields.${index}.enabledIf`}
              label="Habilitar somente se"
            />
          </div>
        </div>
      ))}
    </div>
  );
}

const BOOLEAN_DEFAULT_OPTIONS = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' },
] as const;

const CREATE_BEHAVIOR_OPTIONS = [
  { value: 'show', label: 'Exibir' },
  { value: 'hidden', label: 'Esconder' },
] as const;

/** "Esconder" = campo só existe depois de salvo (equivale ao `visibleIf: id exists` do antigo). */
function CreateBehaviorControl({
  control,
  index,
}: {
  control: Control<any>;
  index: number;
}) {
  return (
    <Controller
      control={control}
      name={`layout.fields.${index}.showOnCreate`}
      render={({ field }) => (
        <ToggleButtons
          value={field.value === false ? 'hidden' : 'show'}
          onChange={(next) => field.onChange(next !== 'hidden')}
          options={CREATE_BEHAVIOR_OPTIONS}
        />
      )}
    />
  );
}

const CONDITION_OPERATORS: { value: ConditionOperator; label: string }[] = [
  { value: 'equals', label: 'Igual a' },
  { value: 'notEquals', label: 'Diferente de' },
  { value: 'in', label: 'Um de (a,b,c)' },
  { value: 'notIn', label: 'Nenhum de (a,b,c)' },
  { value: 'exists', label: 'Preenchido / Sim' },
  { value: 'notExists', label: 'Vazio / Não' },
];

const OPERATORS_WITHOUT_VALUE: ConditionOperator[] = ['exists', 'notExists'];

/**
 * Lista de condições (todas precisam ser verdadeiras) sobre outros campos do form — mesma
 * ideia do `visibleIf`/`enabledIf` do admin antigo. Campo `id` = registro já salvo.
 */
function ConditionsInput({
  control,
  name,
  label,
}: {
  control: Control<any>;
  name: string;
  label: string;
}) {
  const conditions = useFieldArray({ control, name });
  const fieldNames: string[] = (useWatch({ control, name: 'layout.fields' }) ?? [])
    .map((f: { name?: string }) => f.name)
    .filter(Boolean);
  const listId = `${name}-fields`;

  return (
    <Field>
      <div className="flex items-center justify-between">
        <FieldLabel>{label}</FieldLabel>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => conditions.append({ field: '', operator: 'equals', value: '' })}
        >
          <Icon name="plus" />
        </Button>
      </div>

      <datalist id={listId}>
        <option value="id">id (registro salvo)</option>
        {fieldNames.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>

      {conditions.fields.length === 0 && (
        <p className="text-xs text-muted-foreground">Sempre.</p>
      )}

      {conditions.fields.map((row, i) => (
        <ConditionRow
          key={row.id}
          control={control}
          name={`${name}.${i}`}
          listId={listId}
          onRemove={() => conditions.remove(i)}
        />
      ))}
    </Field>
  );
}

function ConditionRow({
  control,
  name,
  listId,
  onRemove,
}: {
  control: Control<any>;
  name: string;
  listId: string;
  onRemove: () => void;
}) {
  const operator: ConditionOperator = useWatch({ control, name: `${name}.operator` });

  return (
    <div className="grid grid-cols-12 gap-2 items-center">
      <Input
        className="col-span-4"
        list={listId}
        placeholder="campo"
        {...control.register(`${name}.field`)}
      />
      <div className="col-span-4">
        <Controller
          control={control}
          name={`${name}.operator`}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {CONDITION_OPERATORS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        />
      </div>
      {OPERATORS_WITHOUT_VALUE.includes(operator) ? (
        <div className="col-span-3" />
      ) : (
        <Input
          className="col-span-3"
          placeholder="valor"
          {...control.register(`${name}.value`)}
        />
      )}
      <Button
        type="button"
        size="icon"
        variant="destructive"
        icon="trash-2"
        className="col-span-1"
        onClick={onRemove}
      />
    </div>
  );
}

function FieldExtras({ control, index }: { control: Control<any>; index: number }) {
  const type: FieldType = useWatch({ control, name: `layout.fields.${index}.type` });

  return (
    <>
      {(type === 'relation' || type === 'multi-relation') && (
        <RelationInputs control={control} listName="fields" index={index} />
      )}
      {MASKABLE_TYPES.includes(type) && (
        <MaskListInput control={control} name={`layout.fields.${index}.mask`} />
      )}
      {type === 'enum' && (
        <OptionsListInput control={control} name={`layout.fields.${index}.options`} />
      )}
      {type === 'file' && <FileOptionsInputs control={control} index={index} />}
      {type === 'boolean' && (
        <Field className="max-w-56">
          <FieldLabel>Valor inicial (criação)</FieldLabel>
          <Controller
            control={control}
            name={`layout.fields.${index}.defaultValue`}
            render={({ field }) => (
              <ToggleButtons
                value={field.value === true ? 'true' : 'false'}
                onChange={(next) => field.onChange(next === 'true')}
                options={BOOLEAN_DEFAULT_OPTIONS}
              />
            )}
          />
        </Field>
      )}
      {type === 'component' && (
        <Field>
          <FieldLabel>Componente (chave no registry)</FieldLabel>
          <Input
            {...control.register(`layout.fields.${index}.component`)}
            placeholder="ex: address-cep"
          />
        </Field>
      )}
    </>
  );
}

function ColumnTypeControl({ control, index }: { control: Control<any>; index: number }) {
  return (
    <Controller
      control={control}
      name={`layout.columns.${index}.type`}
      render={({ field }) => (
        <TypeSelect value={field.value} onChange={field.onChange} options={FIELD_TYPES} />
      )}
    />
  );
}

function FilterTypeControl({ control, index }: { control: Control<any>; index: number }) {
  return (
    <Controller
      control={control}
      name={`layout.filters.${index}.type`}
      render={({ field }) => (
        <TypeSelect
          value={field.value}
          onChange={field.onChange}
          options={FILTER_TYPES}
        />
      )}
    />
  );
}

function FieldTypeControl({ control, index }: { control: Control<any>; index: number }) {
  return (
    <Controller
      control={control}
      name={`layout.fields.${index}.type`}
      render={({ field }) => (
        <TypeSelect value={field.value} onChange={field.onChange} options={FIELD_TYPES} />
      )}
    />
  );
}

function WidthControl({ control, index }: { control: Control<any>; index: number }) {
  return (
    <Controller
      control={control}
      name={`layout.fields.${index}.width`}
      render={({ field }) => (
        <TypeSelect
          value={String(field.value ?? 4)}
          onChange={(v) => field.onChange(Number(v))}
          options={WIDTH_OPTIONS}
        />
      )}
    />
  );
}

function SortableControl({ control, index }: { control: Control<any>; index: number }) {
  return (
    <Controller
      control={control}
      name={`layout.columns.${index}.sortable`}
      render={({ field }) => (
        <ToggleButtons
          value={field.value ? 'true' : 'false'}
          onChange={(v) => field.onChange(v === 'true')}
          options={[
            { value: 'true', label: 'Ordenável' },
            { value: 'false', label: 'Fixa' },
          ]}
        />
      )}
    />
  );
}

function RequiredControl({ control, index }: { control: Control<any>; index: number }) {
  return (
    <Controller
      control={control}
      name={`layout.fields.${index}.required`}
      render={({ field }) => (
        <ToggleButtons
          value={field.value ? 'true' : 'false'}
          onChange={(v) => field.onChange(v === 'true')}
          options={[
            { value: 'true', label: 'Obrigatório' },
            { value: 'false', label: 'Opcional' },
          ]}
        />
      )}
    />
  );
}

/**
 * Condição de edição por campo: usa os dois booleans existentes (`showOnEdit`/`disabledOnEdit`)
 * como um único controle de 3 estados — "Editável" é o default (ambos os flags "vazios").
 */
function EditBehaviorControl({
  control,
  index,
}: {
  control: Control<any>;
  index: number;
}) {
  return (
    <Controller
      control={control}
      name={`layout.fields.${index}.showOnEdit`}
      render={({ field: showOnEdit }) => (
        <Controller
          control={control}
          name={`layout.fields.${index}.disabledOnEdit`}
          render={({ field: disabledOnEdit }) => {
            const value: EditBehavior =
              showOnEdit.value === false
                ? 'hidden'
                : disabledOnEdit.value
                  ? 'disabled'
                  : 'editable';

            const onChange = (next: EditBehavior) => {
              if (next === 'hidden') {
                showOnEdit.onChange(false);
                disabledOnEdit.onChange(false);
              } else if (next === 'disabled') {
                showOnEdit.onChange(true);
                disabledOnEdit.onChange(true);
              } else {
                showOnEdit.onChange(true);
                disabledOnEdit.onChange(false);
              }
            };

            return (
              <ToggleButtons
                value={value}
                onChange={onChange}
                options={EDIT_BEHAVIOR_OPTIONS}
              />
            );
          }}
        />
      )}
    />
  );
}

function FileOptionsInputs({ control, index }: { control: Control<any>; index: number }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field>
        <FieldLabel>Aceitar (accept, opcional)</FieldLabel>
        <Input
          {...control.register(`layout.fields.${index}.accept`)}
          placeholder="ex: image/*"
        />
      </Field>
      <Field>
        <FieldLabel>Preview</FieldLabel>
        <AvatarPreviewControl control={control} index={index} />
      </Field>
    </div>
  );
}

function AvatarPreviewControl({
  control,
  index,
}: {
  control: Control<any>;
  index: number;
}) {
  return (
    <Controller
      control={control}
      name={`layout.fields.${index}.avatarPreview`}
      render={({ field }) => (
        <ToggleButtons
          value={field.value ? 'true' : 'false'}
          onChange={(v) => field.onChange(v === 'true')}
          options={[
            { value: 'true', label: 'Avatar circular' },
            { value: 'false', label: 'Padrão' },
          ]}
        />
      )}
    />
  );
}

/**
 * Máscaras (ver src/lib/mask.ts) — digita uma e aperta Enter pra adicionar, podendo ter
 * várias (ex: telefone fixo vs celular). No front elas formatam a exibição/digitação; o
 * backend sempre recebe/grava o valor sem máscara.
 */
function MaskListInput({ control, name }: { control: Control<any>; name: string }) {
  const [draft, setDraft] = useState('');

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const masks: string[] = field.value ?? [];

        const addMask = () => {
          const value = draft.trim();
          if (!value || masks.includes(value)) return;
          field.onChange([...masks, value]);
          setDraft('');
        };

        const removeMask = (mask: string) => {
          field.onChange(masks.filter((m) => m !== mask));
        };

        return (
          <Field>
            <FieldLabel>Máscaras (opcional — ex: (99) 9999-9999)</FieldLabel>
            <div className="flex items-center gap-2">
              <Input
                value={draft}
                placeholder="Digite a máscara e aperte Enter"
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addMask();
                  }
                }}
              />
              <Button type="button" size="sm" variant="outline" onClick={addMask}>
                <Icon name="plus" />
              </Button>
            </div>
            {masks.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {masks.map((m) => (
                  <Badge key={m} variant="secondary" className="flex items-center gap-1">
                    {m}
                    <button type="button" onClick={() => removeMask(m)}>
                      <Icon name="x" className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </Field>
        );
      }}
    />
  );
}

/** Opções de `type: 'enum'` (fields/columns) ou `type: 'select'` sem relação (filters). */
function OptionsListInput({ control, name }: { control: Control<any>; name: string }) {
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const options: { label: string; value: string }[] = field.value ?? [];

        const addOption = () => {
          if (!label.trim() || !value.trim()) return;
          if (options.some((o) => o.value === value.trim())) return;
          field.onChange([...options, { label: label.trim(), value: value.trim() }]);
          setLabel('');
          setValue('');
        };

        const removeOption = (v: string) => {
          field.onChange(options.filter((o) => o.value !== v));
        };

        return (
          <Field>
            <FieldLabel>Opções (label / valor)</FieldLabel>
            <div className="flex items-center gap-2">
              <Input
                value={label}
                placeholder="Label (ex: Ativo)"
                onChange={(e) => setLabel(e.target.value)}
              />
              <Input
                value={value}
                placeholder="Valor (ex: active)"
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addOption();
                  }
                }}
              />
              <Button type="button" size="sm" variant="outline" onClick={addOption}>
                <Icon name="plus" />
              </Button>
            </div>
            {options.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {options.map((o) => (
                  <Badge
                    key={o.value}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {o.label} <span className="text-muted-foreground">({o.value})</span>
                    <button type="button" onClick={() => removeOption(o.value)}>
                      <Icon name="x" className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </Field>
        );
      }}
    />
  );
}

/**
 * Em `filters`, relação se aplica a `type: 'select'` (alternativa às `options` estáticas).
 * Em `fields`, relação é `type: 'relation'` (N:1) ou `type: 'multi-relation'` (N:N — vira
 * checklist na tela de cadastro, salvando via rota aninhada `PUT /:resource/:id/:related`).
 * Em `columns`, só `type: 'multi-relation'` faz sentido (vira badges no grid).
 * Deixado em branco = sem relação (`options`/select comum), pra não conflitar com o estático.
 */
function RelationInputs({
  control,
  listName,
  index,
}: {
  control: Control<any>;
  listName: 'columns' | 'filters' | 'fields';
  index: number;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field>
        <FieldLabel>Recurso relacionado (opcional)</FieldLabel>
        <Input
          {...control.register(`layout.${listName}.${index}.relation.resource`)}
          placeholder="ex: payment-methods"
        />
      </Field>
      <Field>
        <FieldLabel>Campo exibido no select</FieldLabel>
        <Input
          {...control.register(`layout.${listName}.${index}.relation.displayField`)}
          placeholder="ex: description"
        />
      </Field>
      <Field>
        <FieldLabel>Ordenar opções por (opcional)</FieldLabel>
        <Input
          {...control.register(`layout.${listName}.${index}.relation.orderBy`, {
            setValueAs: (v) => (v ? v : undefined),
          })}
          placeholder="ex: date"
        />
      </Field>
      <Field>
        <FieldLabel>Direção</FieldLabel>
        <Controller
          control={control}
          name={`layout.${listName}.${index}.relation.order`}
          render={({ field }) => (
            <ToggleButtons
              value={field.value ?? 'asc'}
              onChange={field.onChange}
              options={SORT_DIRECTION_OPTIONS}
            />
          )}
        />
      </Field>
    </div>
  );
}

const SORT_DIRECTION_OPTIONS = [
  { value: 'asc', label: 'Crescente' },
  { value: 'desc', label: 'Decrescente' },
] as const;

export type { ScreenLayout };
